import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpParams,} from '@angular/common/http';
import {
  AnalysisDemographicFilters,
  AnalysisQueryRequired,
  CategoryScoresApiResponse,
  CategoryScoresRequest,
  DimensionScoresApiResponse,
  DimensionScoresRequest,
  DomainScoresApiResponse,
  DomainScoresRequest,
  FinalAssessmentsApiResponse,
  FinalAssessmentsRequest,
  normalizeDomainName,
} from '../infrastructure/analysis.api.types';
import {catchError, map, Observable, throwError} from 'rxjs';
import {environment} from '@/environments/environment';
import {DemographicsOptionsService} from '../components/filters-report/service/demographics-options.service';

type AnalysisRequest = AnalysisQueryRequired & AnalysisDemographicFilters;

/** Nuevo: request flexible para Final Assessments con 0/1/N groupBy */
type FinalAssessmentsGroupedRequest = FinalAssessmentsRequest & {
  /** Puede ser vacío, string, array o CSV (si llega como string ya CSV lo respetamos). */
  groupBy?: string | keyof AnalysisDemographicFilters | string[];
};

@Injectable({providedIn: 'root'})
export class AnalysisService {
  private http = inject(HttpClient);
  private catalogs = inject(DemographicsOptionsService);
  private apiUrl = `${environment.apiUrl}/analysis`;

  // ===========================
  getCategoryScores(
    req: CategoryScoresRequest
  ): Observable<CategoryScoresApiResponse> {
    const params = this.buildParams(req);
    return this.http
      .get<CategoryScoresApiResponse>(`${this.apiUrl}/category-scores`, {
        params,
      })
      .pipe(
        map((data) => this.applyCorrections(data)),
        map((data) =>
          data.map((item) => ({
            ...item,
            categoryName: normalizeDomainName(item.categoryName),
          }))
        )
      );
  }

  // ===========================
  //         Category

  // ===========================
  getDimensionScores(
    req: DimensionScoresRequest
  ): Observable<DimensionScoresApiResponse> {
    const params = this.buildParams(req);
    return this.http
      .get<DimensionScoresApiResponse>(`${this.apiUrl}/dimension-scores`, {
        params,
      })
      .pipe(
        map((data) => this.applyCorrections(data)),
        map((data) =>
          data.map((item) => ({
            ...item,
            dimensionName: normalizeDomainName(item.dimensionName),
          }))
        )
      );
  }

  // ===========================
  //         Dimension

  // ===========================
  getDomainScores(
    req: DomainScoresRequest
  ): Observable<DomainScoresApiResponse> {
    const params = this.buildParams(req);
    return this.http
      .get<DomainScoresApiResponse>(`${this.apiUrl}/domain-scores`, {params})
      .pipe(
        map((data) => this.applyCorrections(data)),
        map((data) =>
          data.map((item) => ({
            ...item,
            domainName: normalizeDomainName(item.domainName),
          }))
        )
      );
  }

  // ===========================
  //          Domain
  // ===========================

  /**  una sola vía para 0/1/N groupBy */
  getFinalAssessmentsGrouped(
    req: FinalAssessmentsGroupedRequest
  ): Observable<FinalAssessmentsApiResponse> {
    const params = this.buildParams(req as any);
    return this.http
      .get<FinalAssessmentsApiResponse>(`${this.apiUrl}/final-assessments`, {
        params,
      })
      .pipe(map(data => this.applyCorrections(data)), catchError(this.handleError));
  }

  // ===========================
  //     Final Assessments
  // ===========================

  /** Wrapper: sin agrupación (lo dejamos para compatibilidad) */
  getFinalAssessments(
    req: FinalAssessmentsRequest
  ): Observable<FinalAssessmentsApiResponse> {
    return this.getFinalAssessmentsGrouped(req);
  }

  /** Wrapper: con 1 groupBy (compatibilidad) */
  getFinalAssessmentsByGroup(
    req: FinalAssessmentsRequest & { groupBy: keyof AnalysisDemographicFilters }
  ): Observable<FinalAssessmentsApiResponse> {
    return this.getFinalAssessmentsGrouped(req as any);
  }

  /** Wrapper: con CSV (compatibilidad) */
  getFinalAssessmentsMultiGroup(
    req: FinalAssessmentsRequest & { groupByCsv: string }
  ): Observable<FinalAssessmentsApiResponse> {
    const {groupByCsv, ...rest} = req;
    return this.getFinalAssessmentsGrouped({
      ...rest,
      groupBy: groupByCsv,
    } as any);
  }

  private applyCorrections<T extends any[]>(data: T): T {
    // Si los catálogos aún no están cargados, devolvemos los datos sin cambios.
    if (!this.catalogs.catalogsLoaded()) {
      return data;
    }
    // Función auxiliar para crear un mapa de corrección.
    // La clave es el valor del catálogo (sin acentos, minúsculas) y el valor es el valor original.
    const createCorrectionMap = (options: { value: string; label: string }[]) =>
      new Map(
        options.map((o) => [
          o.value
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase(),
          o.value,
        ])
      );

    // Creamos los mapas de corrección para 'departmento' y 'occupación'.
    const departmentCorrectionMap = createCorrectionMap(
      this.catalogs.getDepartment()
    );
    const occupationCorrectionMap = createCorrectionMap(
      this.catalogs.getOccupation()
    );

    // Iteramos sobre cada resultado para aplicar correcciones.
    return data.map((item: any) => {
      // Creamos una copia del objeto para no mutar el original
      const correctedItem = {...item};

      // Corrección de Departamento
      if (item.department && typeof item.department === 'string') {
        const normalized = item.department
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase();
        if (departmentCorrectionMap.has(normalized)) {
          // Si encontramos una coincidencia en el mapa, usamos el valor original del catálogo.
          correctedItem.department = departmentCorrectionMap.get(normalized);
        }
      }

      // Corrección de Ocupación
      if (item.occupation && typeof item.occupation === 'string') {
        const normalized = item.occupation
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase();
        if (occupationCorrectionMap.has(normalized)) {
          // Si encontramos una coincidencia en el mapa, usamos el valor original del catálogo.
          correctedItem.occupation = occupationCorrectionMap.get(normalized);
        }
      }

      return correctedItem;
    }) as T;
  }

  // =====================================
  //      Construcción de parametros

  // ====================================
  private buildParams(
    req: AnalysisRequest & {
      groupBy?: string | keyof AnalysisDemographicFilters | string[];
    }
  ): HttpParams {
    let params = new HttpParams()
      .set('guideId', req.guideId)
      .set('campaign', req.campaign);

    // admitir array/string/CSV
    if (req.groupBy) {
      const gb = Array.isArray(req.groupBy)
        ? req.groupBy.join(',')
        : String(req.groupBy);
      params = params.set('groupBy', gb);
    }

    // campos opcionales
    const optionalStringKeys = [
      'gender',
      'ageRange',
      'department',
      'educationLevel',
      'position',
      'employeeType',
      'seniority',
      'contractType',
      'maritalStatus',
      'educationStatus',
      'occupation',
      'workSchedule',
      'totalWorkExperience',
    ] as const;

    optionalStringKeys.forEach((key) => {
      const value = req[key];
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });

    if (typeof req.hasShiftRotation === 'boolean') {
      params = params.set('hasShiftRotation', String(req.hasShiftRotation));
    }

    return params;
  }

  private handleError(err: HttpErrorResponse): Observable<never> {
    const msg =
      (err?.error && (err.error.message || err.error.error)) ||
      err.message ||
      'Ocurrió un error.';
    return throwError(() => new Error(msg));
  }
}
